import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { RiArrowDownSLine } from 'react-icons/ri';

import styles from './index.module.scss';

const cx = classNames.bind(styles);

const GAP = 6;

const Select = ({ name, value, onChange, readOnly = false, disabled = false, className, align = 'left', children }) => {
  const [isOpen, setOpen] = useState(false);
  const selectRef = useRef();
  const triggerRef = useRef();
  const dropdownRef = useRef();

  // 부모 요소 중 transform 속성을 가진 요소 찾기
  const findTransformParent = useCallback(() => {
    let element = selectRef.current?.parentElement;

    while (element) {
      const style = window.getComputedStyle(element);
      if (style.transform && style.transform !== 'none') {
        return element;
      }
      element = element.parentElement;
    }

    return null;
  }, []);

  // Extract options from children
  const options = React.Children.toArray(children).map(({ props }) => ({
    value: props.value,
    label: props.children,
    hidden: props.hidden,
    disabled: props.disabled,
  }));

  const calculateDropdownPosition = useCallback(() => {
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();

    // Dropdown 가로 크기 정의
    const dropdownWidth = Math.max(triggerRect.width, dropdownRect.width);
    dropdownRef.current.style.width = `${dropdownWidth}px`;

    // 아래 공간이 충분한지 확인
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const showBelow = spaceBelow >= dropdownRect.height + 56 + GAP; // Dropdown 높이 + BottomBar 높이 + 간격

    let top = triggerRect.top;
    if (showBelow) {
      top += triggerRect.height + GAP;
    } else {
      top -= dropdownRect.height + GAP;
    }

    let left = triggerRect.left;
    if (align === 'right') {
      left = triggerRect.right - dropdownWidth;
    }

    // transform 부모가 있으면 부모 좌표만큼 보정
    const transformParent = findTransformParent();
    if (transformParent) {
      const parentRect = transformParent.getBoundingClientRect();
      top -= parentRect.top;
      left -= parentRect.left;
    }

    dropdownRef.current.style.top = `${top}px`;
    dropdownRef.current.style.left = `${left}px`;
  }, [findTransformParent]);

  useUpdateEffect(() => {
    const handleClickOutside = (e) => {
      if (!selectRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    const handleResize = () => {
      setOpen(false);
    };

    const handleScroll = (e) => {
      // dropdownRef 요소 내부에서 발생한 스크롤은 무시
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true); // capture phase에서 감지
      calculateDropdownPosition();

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, calculateDropdownPosition]);

  const handleToggle = useCallback(
    (e) => {
      e.stopPropagation();

      if (!disabled) {
        setOpen(!isOpen);
      }
    },
    [disabled, isOpen],
  );

  const handleOptionClick = useCallback(
    (e, option) => {
      e.stopPropagation();

      if (option.disabled) return;

      setOpen(false);

      if (onChange) {
        onChange({ target: { name, value: option.value } });
      }
    },
    [onChange, name],
  );

  const displayValue = useMemo(() => {
    if (value === null || value === undefined || value === '') return 'Select';

    const option = options.find(({ value: optionValue }) => optionValue === value);
    return option ? option.label : 'Select';
  }, [value, options]);

  return (
    <div ref={selectRef} className={cx('container')}>
      <div ref={triggerRef} className={cx(['trigger', { readOnly, disabled }, className])} onClick={handleToggle}>
        <span className={cx('value')}>{displayValue}</span>
        <RiArrowDownSLine className={cx(['arrow', { rotated: isOpen }])} size={16} />
      </div>
      <div ref={dropdownRef} className={cx(['dropdown', { open: isOpen }])}>
        <ul className={cx('options')}>
          {options.map((option) => {
            const isSelected = value === option.value;

            return (
              <li
                key={option.value}
                className={cx([
                  'option',
                  {
                    hidden: option.hidden,
                    disabled: option.disabled,
                    selected: isSelected,
                  },
                ])}
                onClick={(e) => handleOptionClick(e, option)}>
                <span className={cx('label')}>{option.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

Select.propTypes = {
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  align: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node,
};

export default Select;
